/**
 * NGADataService - Servicio para el Nomenclátor Geográfico de Andalucía
 * 
 * Proporciona acceso a 233K+ topónimos de Andalucía con:
 * - Búsqueda por nombre (fuzzy matching con uFuzzy)
 * - Filtrado por tipo INSPIRE (landform, populatedplace, hydrography, etc.)
 * - Búsqueda espacial por proximidad (Flatbush)
 * - Caché LRU para búsquedas frecuentes
 * 
 * Datos: WFS-INSPIRE IECA - Nomenclátor Geográfico de Andalucía
 * CRS: EPSG:25830 (UTM30 ETRS89)
 * 
 * @module services/data/NGADataService
 * @version 1.0.0
 * @date 2026-01-29
 */

import uFuzzy from '@leeoniya/ufuzzy';
import Flatbush from 'flatbush';
import { getR2DataService, type FetchResult as _FetchResult } from './R2DataService';
import { fetchWithBackoff } from '../../utils/fetchWithBackoff';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';

/**
 * URL base del Worker R2 (alias local).
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE`).
 */
const R2_BASE_URL = WORKER_BASE;

/** Timeout para peticiones R2 por municipio (ms) */
const R2_MUNICIPALITY_TIMEOUT = 15000;

/** Máximo de municipios en caché LRU */
const MAX_CACHED_MUNICIPALITIES = 10;

// =============================================================================
// TIPOS
// =============================================================================

/** Feature NGA individual */
export interface NGAFeature {
  /** ID único del topónimo */
  id: string;
  /** Nombre del lugar */
  name: string;
  /** Tipo INSPIRE (landform, populatedplace, hydrography, etc.) */
  type: string;
  /** Tipo local en español (Cueva, Cortijo, Arroyo, etc.) */
  localType: string;
  /** Coordenada X UTM30 ETRS89 */
  x: number;
  /** Coordenada Y UTM30 ETRS89 */
  y: number;
}

/** Dataset NGA completo */
export interface NGADataset {
  version: string;
  source: string;
  crs: string;
  count: number;
  features: NGAFeature[];
}

/** Tipos INSPIRE del NGA */
export type NGAInspireType = 
  | 'landform'
  | 'populatedplace'
  | 'hydrography'
  | 'landcover'
  | 'transportnetwork'
  | 'administrativeunit'
  | 'protectedsite'
  | 'building'
  | 'other';

/** Resultado de búsqueda NGA */
export interface NGASearchResult {
  /** Feature encontrado */
  feature: NGAFeature;
  /** Puntuación de coincidencia (0-1, mayor = mejor) */
  score: number;
  /** Distancia en metros (solo para búsqueda espacial) */
  distance?: number;
}

/** Opciones de búsqueda */
export interface NGASearchOptions {
  /** Límite de resultados (default: 10) */
  limit?: number;
  /** Filtrar por tipos INSPIRE */
  types?: NGAInspireType[];
  /** Filtrar por tipos locales (Cueva, Cortijo, etc.) */
  localTypes?: string[];
  /** Umbral mínimo de score (0-1, default: 0.3) */
  minScore?: number;
}

/** Opciones de búsqueda espacial */
export interface NGASpatialSearchOptions extends NGASearchOptions {
  /** Radio máximo en metros (default: 5000) */
  maxDistance?: number;
}

/** Estadísticas del servicio */
export interface NGAServiceStats {
  /** Total de features cargados (legacy + municipios) */
  totalFeatures: number;
  /** Features por tipo INSPIRE */
  featuresByType: Record<string, number>;
  /** Estado de índices */
  indexStatus: {
    fuzzy: boolean;
    spatial: boolean;
  };
  /** Hits de caché de búsqueda */
  searchCacheHits: number;
  /** Fuente de datos actual (legacy) */
  dataSource: 'cache' | 'r2' | 'static' | null;
  /** Número de municipios cargados via loadMunicipality */
  municipalitiesLoaded: number;
}

/** Respuesta JSON de un fichero NGA por municipio en R2 */
interface NGAMunicipalityResponse {
  version?: string;
  source?: string;
  crs?: string;
  municipio?: string;
  ine_mun?: string;
  count?: number;
  features: NGAFeature[];
}

/** Municipio NGA cargado con índices */
interface LoadedNGAMunicipality {
  features: NGAFeature[];
  fuzzyIndex: uFuzzy;
  fuzzyHaystack: string[];
  spatialIndex: Flatbush;
  loadedAt: number;
}

// =============================================================================
// CONSTANTES
// =============================================================================

/** Tamaño de caché LRU para búsquedas */
const SEARCH_CACHE_SIZE = 100;

/** Configuración uFuzzy para nombres españoles */
/** 
 * Configuración uFuzzy para nombres españoles
 * - interIns alto para tolerar artículos (del, de la, etc.)
 * - intraMode 1 para permitir errores tipográficos
 */
const UFUZZY_OPTIONS: uFuzzy.Options = {
  intraMode: 1,
  intraIns: 1,
  interIns: 6,  // Aumentado: tolera "del", "de la", etc. entre palabras
  intraSub: 1,
  intraTrn: 1,
  intraDel: 1,
};

// =============================================================================
// CACHÉ LRU SIMPLE
// =============================================================================

class LRUCache<K, V> {
  private cache = new Map<K, V>();
  private readonly maxSize: number;
  public hits = 0;

  constructor(maxSize: number) {
    this.maxSize = maxSize;
  }

  get(key: K): V | undefined {
    const value = this.cache.get(key);
    if (value !== undefined) {
      // Move to end (most recently used)
      this.cache.delete(key);
      this.cache.set(key, value);
      this.hits++;
    }
    return value;
  }

  set(key: K, value: V): void {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.maxSize) {
      // Delete oldest (first item)
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== undefined) {
        this.cache.delete(firstKey);
      }
    }
    this.cache.set(key, value);
  }

  clear(): void {
    this.cache.clear();
    this.hits = 0;
  }
}

// =============================================================================
// SERVICIO PRINCIPAL
// =============================================================================

/**
 * Servicio para búsqueda y acceso al Nomenclátor Geográfico de Andalucía.
 * 
 * Carga datos por municipio desde R2 Worker bajo la ruta:
 *   /nga/{ine5}/nga-{ine5}.json.gz  (CON subdirectorio)
 * 
 * Mantiene caché LRU de municipios con índices fuzzy y espaciales.
 */
export class NGADataService {
  /** Caché LRU de municipios cargados, clave = codINE */
  private municipalityCache = new Map<string, LoadedNGAMunicipality>();
  /** Orden de acceso para evicción LRU */
  private accessOrder: string[] = [];
  /** Caché de búsquedas recientes */
  private searchCache = new LRUCache<string, NGASearchResult[]>(SEARCH_CACHE_SIZE);

  // =========================================================================
  // CARGA POR MUNICIPIO
  // =========================================================================

  /**
   * Construye la URL R2 para un municipio NGA.
   * Ruta: /nga/{ine5}/nga-{ine5}.json.gz (CON subdirectorio)
   */
  static buildR2Url(codINE: string): string {
    return `${R2_BASE_URL}/nga/${codINE}/nga-${codINE}.json.gz`;
  }

  /**
   * Carga datos NGA de un municipio por código INE.
   * Consulta R2 y construye índices fuzzy + espacial.
   */
  async loadMunicipality(codINE: string): Promise<NGAFeature[]> {
    // Comprobar caché
    const cached = this.municipalityCache.get(codINE);
    if (cached) {
      this.updateAccessOrder(codINE);
      return cached.features;
    }

    const features = await this.fetchMunicipalityData(codINE);
    if (features.length === 0) {
      console.warn(`[NGADataService] Sin topónimos para municipio ${codINE}`);
      return [];
    }

    // Construir índices
    const fuzzyHaystack = features.map(f =>
      `${f.name} ${f.localType}`.toLowerCase()
    );
    const fuzzyIndex = new uFuzzy(UFUZZY_OPTIONS);

    let spatialIndex: Flatbush | null = null;
    if (features.length > 0) {
      spatialIndex = new Flatbush(features.length);
      for (const f of features) {
        spatialIndex.add(f.x, f.y, f.x, f.y);
      }
      spatialIndex.finish();
    }

    this.addToCache(codINE, {
      features,
      fuzzyIndex,
      fuzzyHaystack,
      spatialIndex: spatialIndex!,
      loadedAt: Date.now(),
    });

    console.log(
      `[NGADataService] Cargado ${codINE}: ${features.length} topónimos`
    );
    return features;
  }

  /**
   * Descarga datos de municipio NGA: R2 primero, fallback a estático.
   */
  private async fetchMunicipalityData(codINE: string): Promise<NGAFeature[]> {
    // Intento 1: R2 Worker
    try {
      const url = NGADataService.buildR2Url(codINE);
      const { response } = await fetchWithBackoff(url, { timeoutMs: R2_MUNICIPALITY_TIMEOUT });

      if (response.ok) {
        const data: NGAMunicipalityResponse = await response.json();
        return data.features ?? [];
      }
    } catch {
      console.warn(`[NGADataService] R2 falló para ${codINE}, intentando estático`);
    }

    // Intento 2: bundle estático local
    try {
      const staticUrl = `/data/nga/${codINE}/nga-${codINE}.json`;
      const response = await fetch(staticUrl);
      if (response.ok) {
        const data: NGAMunicipalityResponse = await response.json();
        return data.features ?? [];
      }
    } catch {
      // Sin datos disponibles
    }

    console.warn(`[NGADataService] Sin datos NGA disponibles para ${codINE}`);
    return [];
  }

  // =========================================================================
  // INICIALIZACIÓN LEGACY (compatibilidad con tests existentes)
  // =========================================================================

  /** Referencia a features del municipio activo (para compat) */
  private features: NGAFeature[] = [];
  private dataSource: 'cache' | 'r2' | 'static' | null = null;
  private fuzzyIndex: uFuzzy | null = null;
  private fuzzyHaystack: string[] = [];
  private spatialIndex: Flatbush | null = null;
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  /**
   * Inicializa cargando vía R2DataService (modo legacy/bulk).
   * Mantiene compatibilidad con tests existentes que usan mock de R2DataService.
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._legacyLoadData();
    await this.initPromise;
    this.initialized = true;
  }

  private async _legacyLoadData(): Promise<void> {
    const r2Service = getR2DataService();
    try {
      const result = await r2Service.fetchDataset<NGADataset>('nga/nga-all');
      this.features = result.data.features;
      this.dataSource = result.source;
      this._buildLegacyIndices();
    } catch (error) {
      console.error('[NGADataService] Error cargando datos (legacy):', error);
      throw error;
    }
  }

  private _buildLegacyIndices(): void {
    this.fuzzyHaystack = this.features.map(f =>
      `${f.name} ${f.localType}`.toLowerCase()
    );
    this.fuzzyIndex = new uFuzzy(UFUZZY_OPTIONS);

    if (this.features.length > 0) {
      this.spatialIndex = new Flatbush(this.features.length);
      for (const f of this.features) {
        this.spatialIndex.add(f.x, f.y, f.x, f.y);
      }
      this.spatialIndex.finish();
    }

    console.log(
      `[NGADataService] Cargados ${this.features.length} topónimos desde ${this.dataSource}`
    );
    console.log(
      `[NGADataService] Índice fuzzy construido: ${this.fuzzyHaystack.length} entradas`
    );
    console.log(
      `[NGADataService] Índice espacial construido: ${this.features.length} puntos`
    );
  }

  // =========================================================================
  // BÚSQUEDA POR NOMBRE
  // =========================================================================

  /**
   * Resolución de índices: usa municipio específico si existe, o legacy.
   */
  private resolveIndices(codINE?: string): {
    features: NGAFeature[];
    fuzzyIndex: uFuzzy | null;
    fuzzyHaystack: string[];
    spatialIndex: Flatbush | null;
  } {
    if (codINE) {
      const mun = this.municipalityCache.get(codINE);
      if (mun) return mun;
    }
    return {
      features: this.features,
      fuzzyIndex: this.fuzzyIndex,
      fuzzyHaystack: this.fuzzyHaystack,
      spatialIndex: this.spatialIndex,
    };
  }

  /**
   * Búsqueda por nombre con fuzzy matching.
   * Si se proporciona codINE, busca en datos de ese municipio.
   */
  async searchByName(
    query: string,
    options: NGASearchOptions & { codINE?: string } = {}
  ): Promise<NGASearchResult[]> {
    if (!options.codINE) await this.initialize();

    const { limit = 10, types, localTypes, minScore = 0.3 } = options;
    const { features, fuzzyIndex, fuzzyHaystack } = this.resolveIndices(options.codINE);

    // Verificar caché
    const cacheKey = `name:${query}:${JSON.stringify(options)}`;
    const cached = this.searchCache.get(cacheKey);
    if (cached) return cached;

    if (!fuzzyIndex) throw new Error('Índice fuzzy no inicializado');

    const normalizedQuery = query.toLowerCase().trim();
    const [idxs, _info, order] = fuzzyIndex.search(
      fuzzyHaystack,
      normalizedQuery,
      0,
      limit * 5
    );

    if (!idxs || idxs.length === 0) return [];

    const results: NGASearchResult[] = [];
    for (let i = 0; i < (order?.length ?? idxs.length); i++) {
      const idx = order ? idxs[order[i]] : idxs[i];
      const feature = features[idx];

      if (types?.length && !types.includes(feature.type as NGAInspireType)) continue;
      if (localTypes?.length && !localTypes.some(lt =>
        feature.localType.toLowerCase().includes(lt.toLowerCase())
      )) continue;

      const score = 1 - (i / (order?.length ?? idxs.length));
      if (score < minScore) continue;

      results.push({ feature, score });
      if (results.length >= limit) break;
    }

    this.searchCache.set(cacheKey, results);
    return results;
  }

  /**
   * Búsqueda espacial por proximidad.
   * Si se proporciona codINE, busca en datos de ese municipio.
   */
  async searchByProximity(
    x: number,
    y: number,
    options: NGASpatialSearchOptions & { codINE?: string } = {}
  ): Promise<NGASearchResult[]> {
    if (!options.codINE) await this.initialize();

    const { limit = 10, maxDistance = 5000, types, localTypes, minScore = 0 } = options;
    const { features, spatialIndex } = this.resolveIndices(options.codINE);

    if (!spatialIndex) throw new Error('Índice espacial no inicializado');

    const candidateIdxs = spatialIndex.search(
      x - maxDistance, y - maxDistance, x + maxDistance, y + maxDistance
    );

    const results: NGASearchResult[] = [];
    for (const idx of candidateIdxs) {
      const feature = features[idx];

      if (types?.length && !types.includes(feature.type as NGAInspireType)) continue;
      if (localTypes?.length && !localTypes.some(lt =>
        feature.localType.toLowerCase().includes(lt.toLowerCase())
      )) continue;

      const distance = Math.sqrt((feature.x - x) ** 2 + (feature.y - y) ** 2);
      if (distance > maxDistance) continue;

      const score = 1 - (distance / maxDistance);
      if (score < minScore) continue;

      results.push({ feature, score, distance });
    }

    results.sort((a, b) => (a.distance ?? 0) - (b.distance ?? 0));
    return results.slice(0, limit);
  }

  // =========================================================================
  // GETTERS (compatibilidad legacy)
  // =========================================================================

  async getById(id: string): Promise<NGAFeature | null> {
    await this.initialize();
    return this.features.find(f => f.id === id) ?? null;
  }

  async getByType(type: NGAInspireType): Promise<NGAFeature[]> {
    await this.initialize();
    return this.features.filter(f => f.type === type);
  }

  async getByLocalType(localType: string): Promise<NGAFeature[]> {
    await this.initialize();
    const normalized = localType.toLowerCase();
    return this.features.filter(f =>
      f.localType.toLowerCase().includes(normalized)
    );
  }

  async getLocalTypes(): Promise<string[]> {
    await this.initialize();
    const types = new Set<string>();
    for (const f of this.features) {
      if (f.localType) types.add(f.localType);
    }
    return Array.from(types).sort();
  }

  // =========================================================================
  // ESTADÍSTICAS Y UTILIDADES
  // =========================================================================

  async getStats(): Promise<NGAServiceStats> {
    // Agregar features de todos los municipios cargados + legacy
    const allFeatures = [
      ...this.features,
      ...Array.from(this.municipalityCache.values()).flatMap(m => m.features),
    ];

    const featuresByType: Record<string, number> = {};
    for (const f of allFeatures) {
      const t = f.type || 'other';
      featuresByType[t] = (featuresByType[t] ?? 0) + 1;
    }

    return {
      totalFeatures: allFeatures.length,
      featuresByType,
      indexStatus: {
        fuzzy: this.fuzzyIndex !== null || this.municipalityCache.size > 0,
        spatial: this.spatialIndex !== null || this.municipalityCache.size > 0,
      },
      searchCacheHits: this.searchCache.hits,
      dataSource: this.dataSource,
      municipalitiesLoaded: this.municipalityCache.size,
    };
  }

  /** Código INE de los municipios actualmente en caché */
  getLoadedMunicipalities(): string[] {
    return Array.from(this.municipalityCache.keys());
  }

  clearSearchCache(): void {
    this.searchCache.clear();
  }

  async reload(): Promise<void> {
    this.initialized = false;
    this.initPromise = null;
    this.features = [];
    this.fuzzyIndex = null;
    this.fuzzyHaystack = [];
    this.spatialIndex = null;
    this.searchCache.clear();
    this.municipalityCache.clear();
    this.accessOrder = [];

    await this.initialize();
  }

  // =========================================================================
  // CACHÉ LRU INTERNA
  // =========================================================================

  private updateAccessOrder(codINE: string): void {
    this.accessOrder = this.accessOrder.filter(c => c !== codINE);
    this.accessOrder.push(codINE);
  }

  private addToCache(codINE: string, entry: LoadedNGAMunicipality): void {
    // Evicción LRU si se supera el máximo
    while (this.municipalityCache.size >= MAX_CACHED_MUNICIPALITIES && this.accessOrder.length > 0) {
      const oldest = this.accessOrder.shift()!;
      this.municipalityCache.delete(oldest);
      console.log(`[NGADataService] Evicción LRU: ${oldest}`);
    }
    this.municipalityCache.set(codINE, entry);
    this.updateAccessOrder(codINE);
  }
}

// =============================================================================
// SINGLETON
// =============================================================================

let ngaServiceInstance: NGADataService | null = null;

/**
 * Obtiene instancia singleton del servicio NGA
 */
export function getNGADataService(): NGADataService {
  if (!ngaServiceInstance) {
    ngaServiceInstance = new NGADataService();
  }
  return ngaServiceInstance;
}

/**
 * Resetea instancia singleton (útil para testing)
 */
export function resetNGADataService(): void {
  ngaServiceInstance = null;
}

export default NGADataService;
